import { Button, Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { useRef, useState } from 'react';
import { ICONS } from '../../components/ui/icons';

export interface PageRule { path: string; greeting?: string; suggestions: string[] }
export interface PersonaFields {
  role: string; tone: string | null; language: string; greeting: string | null; avatarUrl: string | null;
  suggestions: string[]; pageRules: PageRule[];
}

const LANGUAGES = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'es', label: 'Espanhol' },
  { value: 'en-US', label: 'Inglês' },
];

/** Uma sugestão por linha (perguntas podem ter vírgula) */
const lines = (v: string) => v.split('\n').map((s) => s.trimStart()).slice(0, 6);

/** Mesma regra do argentic-core (`pathMatches`): prefixo por segmento e `*` como curinga */
function matches(pattern: string, path: string) {
  const p = pattern.trim().toLowerCase();
  if (!p) return false;
  const esc = p.split('*').map((s) => s.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*');
  return new RegExp(`^${esc}${p.endsWith('*') ? '' : '(?:[/?#].*)?$'}`).test(path.trim().toLowerCase());
}

/** Aba Persona: cargo, tom, idioma, foto, saudação, sugestões e saudação por página, com prévia */
export function AgentPersonaFields({ value, name, onChange, readOnly, onUpload }: {
  value: PersonaFields; name: string; onChange: (patch: Partial<PersonaFields>) => void; readOnly?: boolean;
  /** Envia a foto ao R2 (só existe com o agente já salvo) */
  onUpload?: (file: File) => Promise<void>;
}) {
  const [sending, setSending] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [previewPath, setPreviewPath] = useState('/');
  const rules = value.pageRules;
  const setRule = (i: number, patch: Partial<PageRule>) => onChange({ pageRules: rules.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const rule = rules.find((r) => matches(r.path, previewPath));
  const greeting = rule?.greeting || value.greeting || `Olá! Sou ${name || 'o assistente virtual'}. Como posso ajudar?`;
  const suggestions = (rule?.suggestions?.filter(Boolean).length ? rule.suggestions : value.suggestions).filter((s) => s.trim());
  const lang = LANGUAGES.find((l) => l.value === value.language) ?? { value: value.language, label: value.language };

  return (
    <>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Field label="Cargo" description="Como se apresenta">
          <Input onChange={(e) => onChange({ role: e.target.value })} readOnly={readOnly} value={value.role} placeholder="atendente da Loja" />
        </Field>
        <Field label="Tom de voz">
          <Input onChange={(e) => onChange({ tone: e.target.value })} readOnly={readOnly} value={value.tone ?? ''} placeholder="leve, direto e acolhedor" />
        </Field>
        <Field label="Idioma">
          <Select value={(lang)?.value ?? null} items={LANGUAGES} onValueChange={(value: string | null) => onChange({ language: value || 'pt-BR' })} disabled={readOnly} />
        </Field>
      </div>
      <Field label="Foto (opcional)" description={onUpload ? 'Envie uma imagem quadrada (PNG, JPG ou WebP, até 1 MB) ou cole um endereço https' : 'Endereço https da imagem, quadrada. Salve o agente para poder enviar um arquivo'}>
        <div className="flex gap-2">
          <div className="flex-1">
            <Input onChange={(e) => onChange({ avatarUrl: e.target.value })} readOnly={readOnly} value={value.avatarUrl ?? ''} placeholder="https://…/foto.png" />
          </div>
          {onUpload && !readOnly && (
            <>
              <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" hidden
                onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setSending(true); try { await onUpload(f); } finally { setSending(false); } }} />
              <Button variant="secondary" icon={"upload"} loading={sending} onClick={() => fileInput.current?.click()}>Enviar foto</Button>
            </>
          )}
        </div>
      </Field>
      <Field label="Saudação" description="Primeira mensagem do widget">
        <Input onChange={(e) => onChange({ greeting: e.target.value })} readOnly={readOnly} value={value.greeting ?? ''} placeholder="Olá! Como posso ajudar?" />
      </Field>
      <InputArea label="Perguntas sugeridas" description="Uma por linha, até 6" onChange={(e) => onChange({ suggestions: lines(e.target.value) })} readOnly={readOnly} rows={3} value={value.suggestions.join('\n')} />

      <div className="space-y-3" data-testid="agent-page-rules">
        <div>
          <span className="text-xs text-kumo-subtle">Saudação por página</span>
          <div className="text-xs !text-[var(--text-muted)]">Para cada página do site, outra saudação e outras sugestões. /precos vale para /precos e subpáginas; *whatsapp* vale para qualquer endereço com whatsapp. A primeira que casar vale.</div>
        </div>
        {rules.map((r, i) => (
          <div key={i} className="rounded border border-[var(--border)] p-3 space-y-2">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field label="Caminho da página">
                  <Input onChange={(e) => setRule(i, { path: e.target.value })} readOnly={readOnly} value={r.path} placeholder="/precos" />
                </Field>
              </div>
              {!readOnly && <Button variant="ghost" shape="square" icon={ICONS.Trash} aria-label="Remover regra" onClick={() => onChange({ pageRules: rules.filter((_, j) => j !== i) })} />}
            </div>
            <Field label="Saudação nesta página">
              <Input onChange={(e) => setRule(i, { greeting: e.target.value })} readOnly={readOnly} value={r.greeting ?? ''} />
            </Field>
            <InputArea label="Sugestões nesta página" description="Uma por linha; vazio usa as sugestões gerais" onChange={(e) => setRule(i, { suggestions: lines(e.target.value) })} readOnly={readOnly} rows={2} value={r.suggestions.join('\n')} />
          </div>
        ))}
        {!readOnly && rules.length < 30 && (
          <Button variant="secondary" icon={ICONS.Plus} onClick={() => onChange({ pageRules: [...rules, { path: '', suggestions: [] }] })}>Adicionar página</Button>
        )}
      </div>

      <div className="rounded border border-[var(--border)] p-3 space-y-2" data-testid="agent-persona-preview">
        <div className="flex items-center gap-2">
          <span className="text-xs text-kumo-subtle">Prévia do widget na página</span>
          <div className="w-40"><Input onChange={(e) => setPreviewPath(e.target.value)} aria-label="Página da prévia" value={previewPath} /></div>
        </div>
        <div className="flex items-start gap-2">
          {value.avatarUrl?.startsWith('https://')
            ? <img src={value.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            : <div className="h-8 w-8 rounded-full border border-[var(--border-subtle)] bg-[var(--bg-primary)]" />}
          <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm">{greeting}</div>
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {suggestions.map((s) => <span key={s} className="rounded-full border border-[var(--border)] px-2 py-0.5 text-xs">{s}</span>)}
          </div>
        )}
        <div className="text-xs !text-[var(--text-muted)]">{rule ? `Regra usada: ${rule.path}` : 'Sem regra para esta página: saudação e sugestões gerais'}</div>
      </div>
    </>
  );
}
